import get from 'lodash/get';
import getConsent from '../consent';
import segment from '../segment';

// Please Read:
// Monetate works fundamentally different (and worse) to Segment. It requires
// all it's data to be delivered "at once" on page load. This is at odds with
// the Segment model - that sends 'track' events as the user moves about the
// page. Despite our best efforts - trying to get data sent to Monetate
// via Segment has been a hair-pulling disaster. Even once (if) the bugs
// in Segment's analytics.js code are fixed, the fundamental differences in
// usage make will make it impossible to experience 100% of Monetate's features.
// This is the reason why we are *not* using Segment to ferry data to Monetate
// but instead are sending it over directly.
//
// If the situation ever improves - please feel free to nuke this code :-)

// The different page types to send to Monetate.
const MAIN = 'main';
const GALLERY = 'shop_gallery';
const CATALOG = 'shop_catalog';
const PDP = 'shop_pdp';
const CHECKOUT_PAYMENT = 'checkout_payment';
const CHECKOUT_SHIPPING = 'checkout_shipping';
const CHECKOUT_SHIPPING_OPTIONS = 'checkout_shipping_options';
const CHECKOUT_REVIEW = 'checkout_review';
const CHECKOUT_COMPLETE = 'checkout_complete';

const SUBSCRIPTION = 'subscription';
const SIGNIN = 'signin';
const OUR_MISSION = 'our_mission';
const OUR_RESPONSIBILITY = 'our_responsibility';
const OUR_TECHNOLOGY = 'our_technology';
const COMMUNITY = 'community';
const YOUTH_PREVENTION = 'youth_prevention';

const POLL_MS = 500;
const MAX_POLLS = 20;
const SEGMENT_RETRACK_DELAY = 400;

// Regex tests on the URL/pathname that we can use to determine the current
// page type. The ordering matters, so make sure you put specific tests
// before general ones
const PAGE_TYPE_TESTS = [
  { test: /^\/$/, pageType: MAIN },
  { test: /^\/shop$/, pageType: CATALOG },
  { test: /^\/shop\/[^\/]+$/, pageType: GALLERY },
  { test: /^\/shop\/[^\/]+\/[^\/]+$/, pageType: PDP },
  { test: /^\/checkout\/payment/, pageType: CHECKOUT_PAYMENT },
  { test: /^\/checkout\/shipping_addresses/, pageType: CHECKOUT_SHIPPING },
  { test: /^\/checkout\/shipment/, pageType: CHECKOUT_SHIPPING_OPTIONS },
  { test: /^\/checkout\/confirmation$/, pageType: CHECKOUT_REVIEW },
  { test: /^\/checkout\/complete\/[^\/]+$/, pageType: CHECKOUT_COMPLETE },
  { test: /^\/subscription$/, pageType: SUBSCRIPTION },
  { test: /^\/auto-ship$/, pageType: SUBSCRIPTION },
  { test: /^\/mission-values$/, pageType: OUR_MISSION },
  { test: /^\/our-responsibility$/, pageType: OUR_RESPONSIBILITY },
  { test: /^\/our-technology$/, pageType: OUR_TECHNOLOGY },
  { test: /^\/community$/, pageType: COMMUNITY },
  { test: /^\/youth-prevention$/, pageType: YOUTH_PREVENTION },
  { test: /^\/signin$/, pageType: SIGNIN },
];

// Utility to remove the trailing slash from a URL if it exists.
const removeTrailingSlash = str => str.replace(/^(.+)\/$/, '$1');

// Utility to wait for a certain number of milliseconds.
export const waitForMilliseconds = ms => {
  return new Promise(rs => setTimeout(rs, ms));
};

// Takes an array of line-items and a currency and creates an array of "rows".
// These "rows" can then be passed to Monetate as cart contents, or as the
// contents of a completed order.
export const getCartRowsFromLineItems = (lineItems, currency) =>
  lineItems.map(lineItem => ({
    productId: lineItem.sku,
    quantity: lineItem.quantity,
    unitPrice: lineItem.price,
    currency,
  }));

export const getPurchaseRowsFromLineItems = (
  lineItems,
  currency,
  orderNumber
) =>
  getCartRowsFromLineItems(lineItems, currency).map(row => ({
    ...row,
    purchaseId: orderNumber,
  }));

// Takes an array of products, and returns a flattened list of all the
// variants of said products. Only return variants that have active offerings
// (otherwise things like discontinued J1 pods will be included).
export const getVariantsFromProducts = products =>
  products
    .map(product => product.variants)
    .reduce((accum, list) => [...accum, ...list]) //Flatten array of arrays.
    .filter(variant => get(variant, 'variant_offerings.active', []).length > 0);

// Derives the page type from a path using the PAGE_TYPE_TESTS regex
// array defined above.
export const getPageType = pathname => {
  const result = PAGE_TYPE_TESTS.find(({ test }) => test.test(pathname)) || {};
  return result.pageType || null;
};

// A wrapper around the raw Monetate API. Safely handles the case where
// the Monetate script doesn't load and the global 'monetateQ' isn't defined.
export const pushToMonetate = arr => {
  if (global.monetateQ) global.monetateQ.push(arr);
};

// Tells Monetate the current page type.
export const setPageType = pageType =>
  pushToMonetate(['setPageType', pageType]);

// Tells Monetate that we're done sending a current batch of events.
export const track = () => pushToMonetate(['trackData']);

// Tells Monetate that we're currently looking at multiple products.
export const addProducts = skus => pushToMonetate(['addProducts', skus]);

// Tells Monetate that we're currently looking at a specific product.
export const addProductDetails = sku =>
  pushToMonetate(['addProductDetails', [sku]]);

// Tells Monetate the contents of our cart.
export const addCartRows = rows => pushToMonetate(['addCartRows', rows]);

// Tells Monetate the contents of our completed order.
export const addPurchaseRows = rows =>
  pushToMonetate(['addPurchaseRows', rows]);

export const init = async (pathname = global.location.pathname, opts) => {
  // This is gross BTW:
  // In case this script loads first, loop until the Monetate global is
  // initialized. If we exceed the stated maximum number of polls, we can
  // assume that Monetate will never load and we gracefully abort instead.
  const pollMs = get(opts, 'pollMs', POLL_MS);
  const maxPolls = get(opts, 'maxPolls', MAX_POLLS);
  var count = 0;
  while (!global.monetateQ) {
    if (count > maxPolls) return;
    await waitForMilliseconds(pollMs);
    count += 1;
  }
  // Normalise the path by removing the trailing slash.
  pathname = removeTrailingSlash(pathname);
  const pageType = getPageType(pathname);
  const currency = get(global.gon, 'currency.iso_code') || 'USD';
  const { marketing } = await getConsent().then(x => x || { marketing: true });
  // If we don't have marketing consent, we should abort as we are not
  // permitted to send any user data to Monetate.
  // If the pageType is falsey, it means we have nothing interesting to send
  // to Monetate and thus should also abort.
  if (!marketing || !pageType) return;
  // Start by sending over the page type.
  setPageType(pageType);

  if (pageType === CATALOG) {
    const variants = getVariantsFromProducts(global.gon.products || []);
    const skus = variants.map(variant => variant.sku);
    addProducts(skus);
  } else if (pageType === GALLERY) {
    // If we're in a gallery page, tell Monetate about the variants that we're
    // currently viewing. Do this by extracting the "category" from the
    // path and using that to filter the global list of variants from gon.
    const category = pathname.split('/').pop();
    const variants = getVariantsFromProducts(global.gon.products || []).filter(
      variant => variant.category === category
    );
    const skus = variants.map(variant => variant.sku);
    addProducts(skus);
  } else if (pageType === PDP) {
    // If we're in a PDP page, tell Monetate about the single variant that
    // we're viewing. Do this by finding the variant that has a URL
    // that matches our current path.
    const variant =
      getVariantsFromProducts(global.gon.products || []).find(
        variant => variant.url === pathname
      ) || {};
    addProductDetails(variant.sku);
  }
  // Tell Monetate about our cart contents - if they exist. Do this by
  // grabbing the current order's line items from gon and transforming
  // them to be compliant with Monetate's API.
  const cartLineItems = get(global.gon, 'current_order.line_items') || [];
  const cartRows = getCartRowsFromLineItems(cartLineItems, currency);
  if (cartRows.length > 0) addCartRows(cartRows);
  // Tell Monetate about our completed order - if it exists. Do this by
  // grabbing the completed order's line items from gon and transforming
  // them to be compliant with Monetate's API.
  const purchaseLineItems =
    get(global.gon, 'checkout.completed_order.line_items') || [];
  const orderNumber = get(global.gon, 'checkout.completed_order.number');
  const purchaseRows = getPurchaseRowsFromLineItems(
    purchaseLineItems,
    currency,
    orderNumber
  );
  if (purchaseRows.length > 0) addPurchaseRows(purchaseRows);

  // Tell Monetate we're done by firing off a track message.
  track();
};

// Certain events (Product Added) are to be handled by Monetate via a Segment
// back-end integration vs. via Analytics.js. Thus, we must make sure the
// Monetate experience is prodded into updating _after_ it has consumed the
// back-end segment event. We add a 400ms delay to enforce that ordering.
// 400ms was advised by Monetate to ensure ~99% of retracks arrive in the
// correct order.
segment.on('track', async () => {
  await waitForMilliseconds(SEGMENT_RETRACK_DELAY);
  track();
});
